/**
 * Build a URL with a query string.
 *
 * @param {string} url - Base URL.
 * @param {Object} params - Query parameters.
 * @return {string}
 * @example
 *
 * buildUrl('https://example.com', { param: 'value', other: 2 });
 * // => 'https://example.com?param=value&other=2'
 */
export function buildUrl(url, params = {}) {
  const queryString = Object.keys(params)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');

  return `${url}?${queryString}`;
}

/**
 * Build a string from parts, filtering out falsy values other than zero.
 *
 * @param {Array} parts - Parts to join.
 * @return {string}
 * @example
 *
 * const conditional = '';
 * buildString(['This is', conditional && `not ${conditional}`, ' included.']);
 * // => 'This is included'
 */
export function buildString(parts) {
  return parts.filter((part) => Boolean(part) || part === 0).join('');
}

/**
 * Callback for string.replace().
 *
 * @ignore
 * @see dashedToCamelCase()
 * @param {Array.<string>} match - Regex matches.
 * @return {string}
 */
function _dashedToCamelCaseReplacer(match) {
  return match[1].toUpperCase();
}

/**
 * Convert a dash-case/kebab-case string to camelCase.
 *
 * @param {string} dashed - Dashed string.
 * @return {string}
 */
export function dashedToCamelCase(dashed) {
  return dashed.replace(/-([a-zA-Z])/g, _dashedToCamelCaseReplacer);
}

/**
 * Convert a camelCase string to dash-case/kebab-case.
 *
 * @param {string} camel - Camel cased string.
 * @return {string}
 */
export function camelCaseToDashed(camel) {
  return camel.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-');
}
